import { Button } from '@/components/ui';

/**
 * The page control every list in the back office uses.
 *
 * One component, not one per screen, for the same reason paging itself is one
 * helper on the server: the thing that has gone wrong here five times is a
 * screen quietly showing a slice, and a slice is only visible to the reader if
 * something on the page says how many rows there really are.
 *
 * So this always prints the range and the total in words, even on a single
 * page. "1 to 50 of 1,057" is the sentence that would have caught the stock
 * screen on the day it started truncating, and it costs one line.
 */
export function Pager({
  page, pages, total, limit, noun = 'row', plural = `${noun}s`, onPage, busy = false,
}: {
  page: number;
  pages: number;
  total: number;
  limit: number;
  /** Singular. "line", "order", "product". Pluralised with an s. */
  noun?: string;
  /** For a noun that is not pluralised with an s: "entry" is "entries", not "entrys" (30 October). */
  plural?: string;
  onPage: (page: number) => void;
  busy?: boolean;
}) {
  const first = total === 0 ? 0 : (page - 1) * limit + 1;
  const last = Math.min(page * limit, total);
  const count = new Intl.NumberFormat('en-AU');

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line
                    bg-raised px-[14px] py-2 text-[12px]">
      <p className="text-muted" aria-live="polite">
        {total === 0
          ? `No ${plural}`
          : `${count.format(first)} to ${count.format(last)} of ${count.format(total)} ${total === 1 ? noun : plural}`}
      </p>

      {pages > 1 && (
        <div className="flex items-center gap-2">
          <Button
            weight="secondary"
            size="sm"
            type="button"
            disabled={page <= 1 || busy}
            onClick={() => onPage(page - 1)}
          >
            Previous
          </Button>
          <span className="font-[family-name:var(--font-mono)] tabular-nums text-muted">
            {count.format(page)} / {count.format(pages)}
          </span>
          <Button
            weight="secondary"
            size="sm"
            type="button"
            disabled={page >= pages || busy}
            onClick={() => onPage(page + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
